import { useState } from 'react';
import { TFunction } from 'react-i18next';
import { logEvent } from '@amplitude/analytics-browser';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CloseOutlined } from '@mui/icons-material';
import Typography from '../../../componentsv2/Typography';
import { COLORS } from '../../../themev2';
import { bankingEvents } from '../../../constants/events';
import { LOCK_RATE_HELP_ARTICLE_URL } from '../../../constants/banking';

const DISMISSED_KEY = 'marco.bankingDashboard.lockRateBannerDismissed';

const isDismissed = () => {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const rememberDismissal = () => {
  try {
    window.localStorage.setItem(DISMISSED_KEY, 'true');
  } catch {
    // Private browsing can reject writes; the banner simply returns next visit.
  }
};

export type LockRateBannerProps = {
  t: TFunction;
};

const LockRateBanner = ({ t }: LockRateBannerProps) => {
  const [isVisible, setIsVisible] = useState(!isDismissed());

  if (!isVisible) {
    return null;
  }

  const handleDismiss = () => {
    rememberDismissal();
    setIsVisible(false);
    logEvent(bankingEvents.LOCK_RATE_BANNER_DISMISS);
  };

  const handleLearnMore = () => {
    logEvent(bankingEvents.LOCK_RATE_BANNER_CLICK_LEARN_MORE);
    window.open(LOCK_RATE_HELP_ARTICLE_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Grid item xs={12}>
      <Grid
        sx={{
          display: 'flex',
          alignItems: 'start',
          gap: '4px',
          backgroundColor: COLORS.indigo[50],
          padding: '16px',
          borderRadius: '12px',
        }}
      >
        <InfoOutlinedIcon sx={{ color: COLORS.indigo[500], height: '20px', marginTop: '2px' }} />
        <Typography
          sx={{
            fontSize: '14px',
            lineHeight: '24px',
            fontWeight: 400,
            color: COLORS.gray[1100],
          }}
        >
          {t('banking-page:dashboard.lockRateBanner.body')}{' '}
          <Box
            component="span"
            onClick={handleLearnMore}
            sx={{
              fontSize: '14px',
              lineHeight: '24px',
              fontWeight: 600,
              color: COLORS.indigo[500],
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {t('banking-page:dashboard.lockRateBanner.linkLabel')}
          </Box>
        </Typography>
        <CloseOutlined
          sx={{
            cursor: 'pointer',
            color: COLORS.indigo[500],
            height: '20px',
            marginTop: '2px',
            marginLeft: 'auto',
          }}
          onClick={handleDismiss}
        />
      </Grid>
    </Grid>
  );
};

export default LockRateBanner;
